/**
 * monetization/types — monetization kit model (canonical MONETIZATION_QUOTAS
 * from listto) with Agape's B2B VARIANT.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * "QUOTA PER ORGANIZATION" VARIANT (reusable delta for the rest of B2B)
 * ══════════════════════════════════════════════════════════════════════════
 * The canonical implementation meters and enforces by `user_id` (CC-40): the billing unit is
 * the PERSON. In Agape the billing unit is the **congregation** (spec §6:
 * "Billing by active congregation"), so the ENTIRE measurement axis changes
 * subject: `subscription`, storage accounting, rejection state, and
 * `measure_usage` are keyed by `organization_id` (the tenant grouping the
 * church tree), not by user.
 *
 * Consequences of the subject change:
 *   - Two accounts in the SAME church share plan, usage, and limits: the treasurer
 *     and pastor see the same counter (what a B2B buyer expects).
 *   - Two churches in different organizations are isolated: A neither sees nor
 *     consumes B's quota (integration test `monetization.rs`).
 *   - The `orgs` key stops counting organizations and counts **live churches
 *     in the tree** — it is the METER for "active congregations", that is, the
 *     quantity used for billing.
 *   - A sync batch can touch multiple organizations (a network admin): the
 *     guard is an `org → counters` map, not a single counter
 *     (`ScopedQuotaGuard` en Rust).
 *
 * TypeScript mirror of the Rust model (`core-rust/src/models/monetization.rs` +
 * `monetization/resolve.rs`): keep BOTH in sync.
 */

/** Canonical quota keys, common to the entire portfolio. */
export const QUOTA_KEYS = ["storage_bytes", "items", "collections", "orgs", "attachments"] as const;

export type QuotaKey = (typeof QUOTA_KEYS)[number];

/** Limits for a tier. `null` = unlimited (env: -1). */
export type QuotaLimits = Record<QuotaKey, number | null>;

/**
 * PRODUCT capabilities unlocked by a tier (spec §6). These are not the
 * ABAC engine capabilities (`songs`, `finances`… by scope): they are the
 * ORTHOGONAL axis of the purchased plan. A treasurer with `finances:write` in their church
 * still needs the congregation to have the plan that includes `finances`.
 *
 * `export` is intentionally in EVERY tier and a test locks that in: data
 * portability is NEVER behind the plan (spec §6).
 */
export const PLAN_FEATURES = [
  /** Export K1 (CSV/PDF). SIEMPRE disponible, incluso en el tier gratis. */
  "export",
  /** Multi-church tree (more than one congregation under the same organization). */
  "multi-church",
  /** Custom roles and granular per-scope grants (beyond org admin). */
  "advanced-permissions",
  /** P2P projection over LAN (code + blind relay + WebRTC). */
  "projection-p2p",
  /** Finances: funds, entries, categories, and records. */
  "finances",
  /** Photo/receipt storage (uploaded to server storage). */
  "photo-storage",
  /** Devotionals with moderator follow-up. */
  "devotional-tracking",
  /** Multi-organization (one designation with multiple tenants). */
  "multi-org",
  /** Federated roles: grants inherited from parent to children. */
  "federated-roles",
  /** Aggregated subtree reports (network roll-up). */
  "subtree-reports",
] as const;

export type PlanFeature = (typeof PLAN_FEATURES)[number];

export interface TierDef {
  /** Id estable ("free", "pro", "network"…). */
  id: string;
  /** Display name. */
  label: string;
  /**
   * true ⇒ tier financed by ads.
   *
   * **Agape is TIER C in `ADS_STRATEGY.md` §9 (institutional product): ZERO
   * ads in every tier.** This field exists because the kit is common to the
   * portfolio, but here it is always `false`, and a static test also checks
   * that no ads SDK enters the bundle.
   */
  adsEnabled: boolean;
  limits: QuotaLimits;
  /** Product capabilities included in the tier. */
  features: string[];
}

/** Resolved configuration (code defaults → env → UI overrides). */
export interface MonetizationConfig {
  /** Master switch; false (default) ⇒ the entire layer is inert. */
  enabled: boolean;
  paymentsEnabled: boolean;
  /** Injected PaymentProvider ID ("none" | "stripe" | …). */
  provider: string;
  /** Tier for a congregation without a subscription. */
  defaultTier: string;
  tiers: TierDef[];
}

/** A persisted override (a `monetization_config` row edited in the admin UI). */
export interface MonetizationOverrideRow {
  key: string;
  value: unknown;
}

/**
 * Real server-side usage, isolated by ORGANIZATION (B2B variant; the canonical
 * implementation isolates by user).
 */
export type UsageSnapshot = Record<QuotaKey, number>;

/** Quota evaluation for the UI (usage bar vs limit). */
export interface QuotaEvaluation {
  key: QuotaKey;
  used: number;
  /** null = unlimited. */
  limit: number | null;
  /** 0..1 (0 if unlimited). */
  ratio: number;
  /** true from 80% of the limit onward. */
  nearLimit: boolean;
  exceeded: boolean;
}
